import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ExternalLink, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { Link, useOutletContext } from 'react-router'

import { ErrorDeLaApi, pedir } from '../api/nucleo'
import { Aviso, Boton, clases, Esqueleto, Tarjeta, Titulo } from '../componentes/ui'
import type { Contexto } from './Modulo'

/**
 * «SEO → Salud del sitio» (CLAUDE.md §10): lo que la web entera tiene que arreglar —enlaces rotos, páginas a las que
 * no llega nadie, lo publicado con peor puntuación y lo que no tiene descripción—, con un enlace a cada documento para
 * arreglarlo. Lo calcula el núcleo (`GET /api/panel/seo/salud`); aquí solo se pinta. No gasta créditos.
 */
type Documento = { coleccion: string; id: number; titulo: string; ruta: null | string }
type Salud = {
  resumen: { indexables: number; puntuacionMedia: null | number; porSemaforo: { bueno: number; mejorable: number; malo: number }; enlacesRotos: number; huerfanas: number; sinDescripcion: number }
  rotos: Array<{ desde: string; texto: null | string; destino: null | string; origen: null | { coleccion: string; id: number; titulo: string } }>
  huerfanas: Documento[]
  peores: Array<Documento & { puntuacion: number }>
  sinDescripcion: Documento[]
  puedeRecalcular: boolean
}

const EDITORES: Record<string, string> = { paginas: 'paginas', entradas: 'entradas', productos: 'productos', servicios: 'servicios' }
const QUE: Record<string, string> = { paginas: 'Página', entradas: 'Entrada', productos: 'Producto', servicios: 'Servicio' }

const colorDe = (p: null | number) => (p === null ? 'text-tenue' : p >= 80 ? 'text-ok' : p >= 50 ? 'text-aviso' : 'text-error')

const Cifra = ({ valor, etiqueta, tono }: { valor: number | string; etiqueta: string; tono?: string }) => (
  <div className="flex flex-col gap-0.5 rounded-campo border border-linea bg-papel px-4 py-3">
    <span className={clases('font-display text-2xl font-semibold', tono)}>{valor}</span>
    <span className="text-[13px] text-tenue">{etiqueta}</span>
  </div>
)

const AbrirDocumento = ({ base, coleccion, id }: { base: string; coleccion: string; id: number }) =>
  EDITORES[coleccion] ? (
    <Link className="inline-flex shrink-0 items-center gap-1 text-[13px] text-violeta hover:underline" to={`${base}/${EDITORES[coleccion]}/${id}`}>
      Abrir <ExternalLink aria-hidden className="size-3.5" />
    </Link>
  ) : null

const Seccion = ({ titulo, explicacion, vacio, hay, children }: { titulo: string; explicacion: string; vacio: string; hay: boolean; children: React.ReactNode }) => (
  <Tarjeta>
    <h2 className="font-display text-base font-semibold">{titulo}</h2>
    <p className="mt-1 text-[13px] text-tenue">{explicacion}</p>
    <div className="mt-3">{hay ? children : <p className="text-sm text-ok">{vacio}</p>}</div>
  </Tarjeta>
)

export const Salud = () => {
  const { espacio, sitio } = useOutletContext<Contexto>()
  const cliente = useQueryClient()
  const clave = ['seo-salud', sitio?.id]
  const { data, isPending, error } = useQuery({ queryKey: clave, enabled: Boolean(sitio), queryFn: () => pedir<Salud>(`/panel/seo/salud?sitio=${sitio!.id}`) })
  const [recalculando, setRecalculando] = useState(false)
  const [aviso, setAviso] = useState<null | { tono: 'error' | 'ok'; texto: string }>(null)

  if (!sitio) return <Aviso tono="error">Esa web no existe, o no es tuya.</Aviso>
  const base = `/e/${espacio}/s/${sitio.id}`
  const titulo = (
    <Titulo descripcion="Lo que tu web entera tiene que arreglar para salir mejor en Google: enlaces que no llevan a ninguna parte, páginas a las que no llega nadie y lo que tiene peor puntuación." rotulo={sitio.nombre}>
      Salud del sitio
    </Titulo>
  )
  if (isPending) return (<>{titulo}<Esqueleto /></>)
  if (error || !data) return (<>{titulo}<Aviso tono="error">No se ha podido leer la salud de tu web. Recarga la página.</Aviso></>)

  const recalcular = async () => {
    setRecalculando(true)
    setAviso(null)
    try {
      const r = await pedir<{ enlaces: number }>('/seo/enlaces/recalcular', { metodo: 'POST', cuerpo: { sitio: sitio.id } })
      await cliente.invalidateQueries({ queryKey: clave })
      setAviso({ tono: 'ok', texto: `Enlaces revisados de nuevo: ${r.enlaces} en toda la web.` })
    } catch (e) {
      setAviso({ tono: 'error', texto: e instanceof ErrorDeLaApi ? e.message : 'No se han podido revisar los enlaces. Vuelve a intentarlo.' })
    } finally {
      setRecalculando(false)
    }
  }

  const r = data.resumen
  return (
    <>
      {titulo}
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Cifra etiqueta="Puntuación media" tono={colorDe(r.puntuacionMedia)} valor={r.puntuacionMedia === null ? '—' : `${r.puntuacionMedia}/100`} />
          <Cifra etiqueta="Páginas que ve Google" valor={r.indexables} />
          <Cifra etiqueta="Enlaces rotos" tono={r.enlacesRotos ? 'text-error' : 'text-ok'} valor={r.enlacesRotos} />
          <Cifra etiqueta="Páginas huérfanas" tono={r.huerfanas ? 'text-aviso' : 'text-ok'} valor={r.huerfanas} />
        </div>
        <p className="text-[13px] text-tenue">
          {r.porSemaforo.bueno} bien · {r.porSemaforo.mejorable} mejorables · {r.porSemaforo.malo} flojas
          {r.sinDescripcion ? ` · ${r.sinDescripcion} sin descripción para Google` : ''}
        </p>
        {aviso && <Aviso tono={aviso.tono}>{aviso.texto}</Aviso>}

        <Seccion
          explicacion="Enlaces de tu web que llevan a una página que ya no existe o que no está publicada. El visitante que los pulsa se encuentra un error."
          hay={data.rotos.length > 0}
          titulo="Enlaces rotos"
          vacio="Ningún enlace roto."
        >
          <ul className="flex flex-col divide-y divide-linea">
            {data.rotos.map((e, i) => (
              <li className="flex flex-wrap items-center justify-between gap-2 py-2" key={`${e.desde}-${i}`}>
                <div className="min-w-0 text-sm">
                  <p className="font-medium">{e.origen?.titulo || e.desde}</p>
                  <p className="truncate text-[13px] text-tenue">
                    En {e.desde}
                    {e.texto ? ` · «${e.texto}»` : ''}
                    {e.destino ? ` → ${e.destino}` : ''}
                  </p>
                </div>
                {e.origen && <AbrirDocumento base={base} coleccion={e.origen.coleccion} id={e.origen.id} />}
              </li>
            ))}
          </ul>
        </Seccion>

        <Seccion
          explicacion="Publicadas, pero ninguna otra página de tu web enlaza a ellas: a Google le cuesta encontrarlas. Enlázalas desde el menú o desde una página parecida."
          hay={data.huerfanas.length > 0}
          titulo="Páginas huérfanas"
          vacio="Todas tus páginas tienen algún enlace que llega a ellas."
        >
          <ul className="flex flex-col divide-y divide-linea">
            {data.huerfanas.map((d) => (
              <li className="flex items-center justify-between gap-2 py-2" key={`${d.coleccion}-${d.id}`}>
                <div className="min-w-0 text-sm">
                  <p className="font-medium">{d.titulo}</p>
                  <p className="truncate text-[13px] text-tenue">
                    {QUE[d.coleccion]} · {d.ruta}
                  </p>
                </div>
                <AbrirDocumento base={base} coleccion={d.coleccion} id={d.id} />
              </li>
            ))}
          </ul>
        </Seccion>

        <Seccion
          explicacion="Lo publicado con peor puntuación de SEO. Al abrir cada una, la pestaña SEO dice qué cambiar; la «Revisión semanal» te propone títulos y descripciones."
          hay={data.peores.length > 0}
          titulo="Lo que más puede mejorar"
          vacio="Todo lo publicado tiene una puntuación de 80 o más."
        >
          <ul className="flex flex-col divide-y divide-linea">
            {data.peores.map((d) => (
              <li className="flex items-center justify-between gap-3 py-2" key={`${d.coleccion}-${d.id}`}>
                <span className={clases('w-14 shrink-0 font-display text-lg font-semibold', colorDe(d.puntuacion))}>{d.puntuacion}</span>
                <div className="min-w-0 flex-1 text-sm">
                  <p className="font-medium">{d.titulo}</p>
                  <p className="truncate text-[13px] text-tenue">
                    {QUE[d.coleccion]} · {d.ruta}
                  </p>
                </div>
                <AbrirDocumento base={base} coleccion={d.coleccion} id={d.id} />
              </li>
            ))}
          </ul>
        </Seccion>

        <Seccion
          explicacion="Sin descripción, Google escribe la suya con un trozo de la página, y casi nunca es la mejor. Escríbela en la pestaña SEO de cada una."
          hay={data.sinDescripcion.length > 0}
          titulo="Sin descripción para Google"
          vacio="Todo lo publicado tiene su descripción."
        >
          <ul className="flex flex-col divide-y divide-linea">
            {data.sinDescripcion.map((d) => (
              <li className="flex items-center justify-between gap-2 py-2" key={`${d.coleccion}-${d.id}`}>
                <div className="min-w-0 text-sm">
                  <p className="font-medium">{d.titulo}</p>
                  <p className="truncate text-[13px] text-tenue">
                    {QUE[d.coleccion]} · {d.ruta}
                  </p>
                </div>
                <AbrirDocumento base={base} coleccion={d.coleccion} id={d.id} />
              </li>
            ))}
          </ul>
        </Seccion>

        {data.puedeRecalcular && (
          <div>
            <Boton cargando={recalculando} icono={<RefreshCw />} onClick={() => void recalcular()}>
              Revisar los enlaces de toda la web
            </Boton>
            <p className="mt-1 text-xs text-tenue">Los enlaces se revisan solos al publicar. Esto los repasa todos ahora.</p>
          </div>
        )}
      </div>
    </>
  )
}
